import { useState } from "react";
import { Link } from "react-router-dom";
import { IconGoogle } from "../components/icons.js";
import { Button } from "../components/ui.js";
import { useAuth } from "../lib/auth.js";
import { APP_ICON, APP_NAME } from "../lib/sport.js";

/**
 * Signing in does not get you into the community — an admin does. This screen says so
 * up front, because "I signed in but I can't see anything" is the support question this
 * two-state model would otherwise generate.
 *
 * Google is the only way in. Everyone with an Android phone already has an account, it
 * needs no mail provider, and one route means one thing to keep working rather than two
 * that both half-work.
 *
 * It also means there is no way in if Google breaks — `[auth.email] enable_signup = false`
 * turns email off for existing accounts too, not just new ones. `signInWithEmail` stays on
 * the context so restoring the form is a small diff, but the provider has to be re-enabled
 * server-side first. The recovery is written down in the README rather than left to be
 * worked out on the day.
 */
export function SignIn() {
  const { signInWithGoogle } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onGoogle() {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (cause) {
      // The redirect normally takes over before this resolves, so reaching here means the
      // provider is misconfigured rather than that the user changed their mind.
      setError(cause instanceof Error ? cause.message : "Couldn't reach Google.");
      setBusy(false);
    }
  }

  return (
    <main
      className="mx-auto max-w-md px-4 pb-16"
      style={{ paddingTop: "calc(env(safe-area-inset-top) + 4rem)" }}
    >
      <img
        src={APP_ICON}
        alt="Prestige Hillside Gateway"
        className="mb-4 size-16 rounded-xl object-contain"
      />
      <div className="micro mb-3">{APP_NAME}</div>
      <h1 className="font-display mb-3 text-3xl leading-tight" style={{ fontWeight: 800 }}>
        Sign in to Prestige Hillside Gateway
      </h1>
      {/* True either way round: whether an admin approves new members is REQUIRE_APPROVAL,
          which only the `join` Edge Function can read, so this screen cannot know which. */}
      <p className="mb-8 text-ink-muted">
        Sign in first, then join. Depending on how this community is set up, an admin may need to
        approve you before you appear on the board.
      </p>

      <div className="tramline mb-8" />

      <Button
        tone="primary"
        className="flex w-full items-center justify-center gap-2.5"
        disabled={busy}
        onClick={() => void onGoogle()}
      >
        <IconGoogle size={20} />
        <span>{busy ? "Taking you to Google…" : "Continue with Google"}</span>
      </Button>

      {error ? (
        <p className="mt-4 text-sm" style={{ color: "var(--color-attention)" }}>
          {error}
        </p>
      ) : null}

      {/* Local development only. Google's client is registered against the deployed
          callback, so the button above cannot work against 127.0.0.1 — and email is off.
          `import.meta.env.DEV` is compile-time, so none of this reaches a build. */}
      {import.meta.env.DEV ? <DevSignIn /> : null}

      {/* On the screen where you decide whether to hand over your Google account, not
          buried somewhere you would have to go looking for it. */}
      <p className="micro mt-10 flex gap-4">
        <Link className="underline" to="/privacy">
          What we store about you
        </Link>
        <span>&middot;</span>
        <Link className="underline" to="/rating">
          How ratings work
        </Link>
      </p>
    </main>
  );
}

/** The seeded roster from supabase/seed.sql. Enough to exercise every role. */
const SEEDED = [
  { email: "alpha@example.test", who: "Alpha · admin" },
  { email: "charlie@example.test", who: "Charlie · organiser" },
  { email: "bravo@example.test", who: "Bravo · player" },
  { email: "echo@example.test", who: "Echo · pending" },
];

function DevSignIn() {
  return (
    <section className="mt-10 border-t-2 pt-5" style={{ borderColor: "var(--color-line-muted)" }}>
      <div className="micro mb-3" style={{ color: "var(--color-attention)" }}>
        Local development only
      </div>
      <div className="flex flex-col gap-2">
        {SEEDED.map((person) => (
          <a
            key={person.email}
            href={`/__dev-login?email=${encodeURIComponent(person.email)}`}
            className="flex items-center px-3"
            style={{
              minHeight: 48,
              borderRadius: "var(--radius-control)",
              border: "1px solid var(--color-line-muted)",
              color: "var(--color-ink)",
            }}
          >
            {person.who}
          </a>
        ))}
      </div>
      <p className="micro mt-3" style={{ textTransform: "none", letterSpacing: 0 }}>
        Signs in through the dev server, which asks gotrue for a link. Not in any build.
      </p>
    </section>
  );
}
